<!-- ═══════════════════════════════════════════════════════════════════════
     FILE: ask-panel.component.html · _40 #223 Anfrage per @ · _40 #218 Alt+P
     Knopf unten links (blinkt bei offenen Anfragen) + Panel mit drei Reitern.
     Testids: ask.launcher · ask.badge · ask.panel · ask.close · ask.tab.{ask|mine|waiting}
              ask.pslot · ask.anchor-type · ask.anchor-id · ask.appeal · ask.suggest
              ask.suggest-opt-{handle} · ask.to · ask.status · ask.imessage · ask.retro
              ask.send · ask.result · ask.mine-item-{id} · ask.reply-{id} · ask.done-{id}
              ask.obsolete-{id} · ask.open-{id} · ask.reply-answer · ask.reply-next
              ask.reply-send · ask.wait-item-{id} · ask.wait-obsolete-{id}
     ═══════════════════════════════════════════════════════════════════ -->

<button type="button" class="ask-launcher" data-testid="ask.launcher"
        [class.ask-launcher--alert]="req.openCount() > 0"
        (click)="toggle()"
        [attr.aria-label]="req.openCount() > 0 ? req.openCount() + ' open requests for you — Alt+P' : 'Ask PIA or a teammate — Alt+P'"
        title="Instead of doing it yourself — ask via Alt+P">
  <span class="ask-launcher__label">Ask</span>
  <kbd class="ask-launcher__kbd">Alt+P</kbd>
  @if (req.openCount() > 0) {
    <span class="ask-launcher__badge" data-testid="ask.badge">{{ req.openCount() }}</span>
  }
</button>

@if (req.panelOpen()) {
  <section class="ask" data-testid="ask.panel" role="dialog" aria-label="Ask and requests">

    <header class="ask__head">
      <nav class="ask__tabs" role="tablist">
        <button type="button" role="tab" class="ask__tab" data-testid="ask.tab.ask"
                [class.is-active]="req.tab() === 'ask'" (click)="setTab('ask')">Ask</button>
        <button type="button" role="tab" class="ask__tab" data-testid="ask.tab.mine"
                [class.is-active]="req.tab() === 'mine'" (click)="setTab('mine')">
          For me @if (req.openCount() > 0) { <span class="ask__count">{{ req.openCount() }}</span> }
        </button>
        <button type="button" role="tab" class="ask__tab" data-testid="ask.tab.waiting"
                [class.is-active]="req.tab() === 'waiting'" (click)="setTab('waiting')">Waiting for</button>
      </nav>
      <button type="button" class="ask__x" data-testid="ask.close" (click)="req.closePanel()" aria-label="Close (Esc)">×</button>
    </header>

    <!-- ── Ask ─────────────────────────────────────────────────────────── -->
    @if (req.tab() === 'ask') {
      <div class="ask__body">
        <div class="ask__row">
          <label class="ask__label" for="ask-pslot">Project</label>
          <select id="ask-pslot" class="ask__input" data-testid="ask.pslot"
                  [ngModel]="pslot()" (ngModelChange)="onPslotChange($event)">
            @if (pslot() && !myProjects().length) { <option [value]="pslot()">{{ pslot() }}</option> }
            @if (!pslot()) { <option value="" disabled>Choose a project</option> }
            @for (p of myProjects(); track p.pslot) { <option [value]="p.pslot">{{ p.project_name || p.pslot }}</option> }
          </select>
        </div>

        <div class="ask__row">
          <label class="ask__label" for="ask-anchor-type">Regarding</label>
          <div class="ask__pair">
            <select id="ask-anchor-type" class="ask__input" data-testid="ask.anchor-type"
                    [ngModel]="anchorType()" (ngModelChange)="anchorType.set($event)">
              @for (t of anchorTypes; track t) { <option [value]="t">{{ t === 'pslot' ? 'n/a (whole project)' : t }}</option> }
            </select>
            @if (anchorType() !== 'pslot') {
              <input class="ask__input ask__input--id" type="number" min="1" placeholder="#" data-testid="ask.anchor-id"
                     [ngModel]="anchorId()" (ngModelChange)="anchorId.set($event)" aria-label="Element number">
            }
          </div>
        </div>

        <div class="ask__row ask__row--appeal">
          <label class="ask__label" for="ask-appeal">Request</label>
          <textarea #appealBox id="ask-appeal" class="ask__input ask__appeal" rows="4" data-testid="ask.appeal"
                    placeholder="Type @ to choose an AI or a teammate — e.g. @pia please check the acceptance criteria"
                    (input)="onAppealInput(appealBox)" (keydown)="onAppealKey($event, appealBox)"
                    (click)="onAppealInput(appealBox)"></textarea>
          @if (suggestQuery() !== null && suggestions().length) {
            <ul class="ask__suggest" data-testid="ask.suggest" role="listbox">
              @for (a of suggestions(); track a.uuid; let i = $index) {
                <li role="option" class="ask__opt" [class.is-active]="i === suggestIndex()"
                    [attr.data-testid]="'ask.suggest-opt-' + a.handle"
                    (mousedown)="$event.preventDefault(); pick(a, appealBox)">
                  <span class="ask__opt-name">{{ a.display_name }}</span>
                  <span class="ask__opt-handle">&#64;{{ a.handle }}</span>
                  @if (a.is_agent) { <span class="ask__tag">AI</span> }
                  @if (agentOf(a); as s) {
                    <span class="ask__light" [ngClass]="'ask__light--' + s.light" [title]="s.hint"
                          [attr.data-testid]="'ask.suggest-light-' + a.handle"></span>
                  }
                </li>
              }
            </ul>
          }
          <p class="ask__to" data-testid="ask.to">
            @if (recipients().length) {
              To: @for (r of recipients(); track r.uuid; let last = $last) {
                <b>{{ r.display_name }}</b>@if (r.is_agent) { <span class="ask__tag">AI</span> }@if (!last) {, }
              }
            } @else {
              <span class="ask__hint">Name at least one person or AI with &#64;.</span>
            }
          </p>
          <!-- 02.11.2026: genannter Agent nicht grün → sagen, was das heißt (KI-Agent-Statusanzeige) -->
          @for (s of notReady(); track s.uuid) {
            <p class="ask__notready" [attr.data-testid]="'ask.notready-' + s.handle">
              <span class="ask__light" [ngClass]="'ask__light--' + s.light"></span>
              &#64;{{ s.handle }}: {{ s.hint }}
              @if (s.open_requests > 0) { {{ s.open_requests }} already waiting. }
              Your request is kept in the queue.
            </p>
          }
        </div>

        <div class="ask__row">
          <label class="ask__label" for="ask-status">Situation</label>
          <input id="ask-status" class="ask__input" data-testid="ask.status" placeholder="Where things stand"
                 [ngModel]="statusStmnt()" (ngModelChange)="statusStmnt.set($event)">
        </div>
        <div class="ask__row">
          <label class="ask__label" for="ask-imessage">Why I ask</label>
          <input id="ask-imessage" class="ask__input" data-testid="ask.imessage" placeholder="What I think, what is unclear to me"
                 [ngModel]="imessage()" (ngModelChange)="imessage.set($event)">
        </div>

        <label class="ask__check">
          <input type="checkbox" data-testid="ask.retro" [ngModel]="retro()" (ngModelChange)="retro.set($event)">
          Retrospective statement — keep open after the element is done
        </label>

        <footer class="ask__foot">
          @if (result(); as r) {
            <p class="ask__result" [class.is-error]="!r.ok" data-testid="ask.result">{{ r.text }}</p>
          }
          <button type="button" class="ask__send" data-testid="ask.send" [disabled]="!canSend()" (click)="send()">
            {{ sending() ? 'Sending…' : 'Send request' }}
          </button>
        </footer>
      </div>
    }

    <!-- ── For me ──────────────────────────────────────────────────────── -->
    @if (req.tab() === 'mine') {
      <div class="ask__body ask__list">
        @if (!req.available()) {
          <p class="ask__empty">Open requests are not available on this instance yet.</p>
        } @else if (!req.open().length) {
          <p class="ask__empty">Nothing waits for your answer.</p>
        }
        @for (q of req.open(); track q.message_id) {
          <article class="req" [class.is-focus]="req.focusId() === q.message_id"
                   [attr.data-testid]="'ask.mine-item-' + q.message_id">
            <p class="req__meta">
              <b>{{ q.from.display_name }}</b>@if (q.from.is_agent) { <span class="ask__tag">AI</span> }
              · {{ q.anchor.type === 'pslot' ? 'project' : q.anchor.type + ' #' + q.anchor.id }}
              · {{ ageOf(q.created_at) }}
              @if (q.request_kind === 'retro') { · <span class="ask__tag">retro</span> }
            </p>
            <p class="req__appeal">{{ q.cflx.appeal }}</p>
            @if (q.cflx.status_stmnt) { <p class="req__ctx">{{ q.cflx.status_stmnt }}</p> }

            @if (replyFor() === q.message_id) {
              <div class="req__reply">
                <textarea class="ask__input" rows="3" data-testid="ask.reply-answer" placeholder="Your answer"
                          [ngModel]="replyAnswer()" (ngModelChange)="replyAnswer.set($event)"></textarea>
                <input class="ask__input" data-testid="ask.reply-next" placeholder="Next step (e.g. please confirm, or: none)"
                       [ngModel]="replyNext()" (ngModelChange)="replyNext.set($event)">
                <div class="req__actions">
                  <button type="button" class="ask__send" data-testid="ask.reply-send"
                          [disabled]="busy() === q.message_id || !replyAnswer().trim() || !replyNext().trim()"
                          (click)="sendReply(q)">Send answer</button>
                  <button type="button" class="req__btn" (click)="replyFor.set(null)">Cancel</button>
                </div>
              </div>
            } @else {
              <div class="req__actions">
                <button type="button" class="req__btn req__btn--main" [attr.data-testid]="'ask.reply-' + q.message_id"
                        [disabled]="!q.post_id" (click)="startReply(q)">Reply</button>
                <button type="button" class="req__btn" [attr.data-testid]="'ask.done-' + q.message_id"
                        [disabled]="busy() === q.message_id" (click)="closeReq(q.message_id, 'done')">Done</button>
                <button type="button" class="req__btn" [attr.data-testid]="'ask.obsolete-' + q.message_id"
                        [disabled]="busy() === q.message_id" (click)="closeReq(q.message_id, 'obsolete')">Obsolete</button>
                @if (anchorLink(q.pslot, q.anchor.type, q.anchor.id)) {
                  <button type="button" class="req__btn req__btn--link" [attr.data-testid]="'ask.open-' + q.message_id"
                          (click)="go(q.pslot, q.anchor.type, q.anchor.id)">Open →</button>
                }
              </div>
            }
          </article>
        }
      </div>
    }

    <!-- ── Waiting for ─────────────────────────────────────────────────── -->
    @if (req.tab() === 'waiting') {
      <div class="ask__body ask__list">
        @if (!waiting().length) { <p class="ask__empty">You are not waiting for any answer.</p> }
        @for (w of waiting(); track w.message_id) {
          <article class="req" [attr.data-testid]="'ask.wait-item-' + w.message_id">
            <p class="req__meta">
              waiting for <b>{{ openRecipients(w) }}</b>
              · {{ w.anchor.type === 'pslot' ? 'project' : w.anchor.type + ' #' + w.anchor.id }}
              · {{ ageOf(w.created_at) }}
            </p>
            <p class="req__appeal">{{ w.appeal }}</p>
            <div class="req__actions">
              <button type="button" class="req__btn" [attr.data-testid]="'ask.wait-obsolete-' + w.message_id"
                      [disabled]="busy() === w.message_id" (click)="closeReq(w.message_id, 'obsolete', true)">Obsolete</button>
              @if (anchorLink(w.pslot, w.anchor.type, w.anchor.id)) {
                <button type="button" class="req__btn req__btn--link" (click)="go(w.pslot, w.anchor.type, w.anchor.id)">Open →</button>
              }
            </div>
          </article>
        }
      </div>
    }
  </section>
}
